import React from 'react';
import { View, Text, StyleSheet, StatusBar } from 'react-native';
import { Button, useTheme } from 'react-native-paper';
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons';
import { useAuth } from '../context/AuthContext';
import { ErrorLogger } from '../utils/errorLogger';
import { colors } from '../theme/theme';

interface LoginScreenProps {
  navigation: any;
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  content: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 20,
  },
  iconContainer: {
    marginBottom: 20,
  },
  title: {
    fontFamily: 'Manrope-Bold',
    fontSize: 32,
    textAlign: 'center',
    marginBottom: 8,
  },
  subtitle: {
    fontFamily: 'Inter-Regular',
    fontSize: 18,
    textAlign: 'center',
    marginBottom: 40,
    opacity: 0.9,
  },
  loginCard: {
    width: '100%',
    maxWidth: 350,
    alignItems: 'baseline',
  },
  welcomeTitle: {
    fontFamily: 'Manrope-SemiBold',
    fontSize: 24,
    marginBottom: 12,
    padding: 10,
  },
  welcomeSubtitle: {
    fontFamily: 'Inter-Regular',
    fontSize: 16,
    marginBottom: 30,
  },
  errorBanner: {
    width: '100%',
    borderRadius: 6,
    padding: 12,
    marginBottom: 16,
  },
  errorText: {
    fontFamily: 'Inter-Medium',
    fontSize: 14,
    textAlign: 'center',
  },
  googleButton: {
    borderWidth: 0,
    borderRadius: 6,
    padding: 5,
    width: '100%',
    height: 50,
    marginBottom: 20,
  },
  googleButtonText: {
    fontFamily: 'Inter-Medium',
    fontSize: 16,
  },
  googleIcon: {
    fontFamily: 'Inter-Medium',
    fontSize: 18,
    color: '#4285F4',
    marginRight: 8,
  },
  termsText: {
    fontFamily: 'Inter-Regular',
    fontSize: 12,
    textAlign: 'center',
    lineHeight: 18,
  },
  linkText: {
    textDecorationLine: 'underline',
  },
});

const GoogleIcon = () => <Text style={styles.googleIcon}>G</Text>;

const LoginScreen: React.FC<LoginScreenProps> = () => {
  const { signInWithGoogle, authError } = useAuth();
  const theme = useTheme();

  const handleGoogleSignIn = async () => {
    try {
      await signInWithGoogle();
      console.log('Sign-in successful, user will be navigated to Dashboard');
    } catch (error) {
      ErrorLogger.logError(error as any, 'LoginScreen.handleGoogleSignIn');
    }
  };

  return (
    <View
      style={{ ...styles.container, backgroundColor: theme.colors.primary }}
      testID="login-container"
    >
      <StatusBar
        barStyle="light-content"
        backgroundColor={theme.colors.primary}
      />
      <View style={styles.content}>
        {/* Truck Icon */}
        <View style={styles.iconContainer}>
          <MaterialCommunityIcons
            name="truck"
            size={64}
            color={theme.colors.onPrimary}
          />
        </View>

        {/* Title */}
        <Text style={[styles.title, { color: theme.colors.onPrimary }]}>
          Anzura
        </Text>
        <Text style={[styles.subtitle, { color: theme.colors.onPrimary }]}>
          Keeping track of our income
        </Text>

        {/* Login Card */}
        <View style={styles.loginCard}>
          {/* Authorization error (e.g. not on the allowlist) */}
          {authError ? (
            <View
              style={[
                styles.errorBanner,
                { backgroundColor: theme.colors.errorContainer },
              ]}
              testID="auth-error"
            >
              <Text
                style={[
                  styles.errorText,
                  { color: theme.colors.onErrorContainer },
                ]}
              >
                {authError}
              </Text>
            </View>
          ) : null}

          {/* Google Sign-in Button */}
          <Button
            mode="outlined"
            style={[
              styles.googleButton,
              {
                backgroundColor: theme.colors.surface,
                borderColor: theme.colors.outline,
              },
            ]}
            labelStyle={{ color: theme.colors.onSurface }}
            onPress={handleGoogleSignIn}
            uppercase={false}
            icon={GoogleIcon}
            testID="google-sign-in-button"
          >
            Sign-in with Google
          </Button>

          {/* Terms */}
          <Text style={[styles.termsText, { color: theme.colors.onPrimary }]}>
            By signing in, you agree to our{' '}
            <Text
              style={[
                styles.linkText,
                { color: colors.onPrimaryContainer },
              ]}
            >
              Terms of Service
            </Text>{' '}
            and{' '}
            <Text
              style={[
                styles.linkText,
                { color: colors.onPrimaryContainer },
              ]}
            >
              Privacy Policy
            </Text>
          </Text>
        </View>
      </View>
    </View>
  );
};

export default LoginScreen;
